import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { getQuestions } from "../../../services/questionService";
import { isCanceledError } from "../../../utils/examError";
import { DifficultyBadge } from "../../Difficulty/Difficulty";
import { QuestionStatusBadge } from "../../QuestionList/questionStatus";
import { focusRing } from "../matrix/MatrixParts";
import { MAX_TASK_QUESTIONS, personName } from "./taskModel";

const PAGE_SIZE = 30;
const fieldClass =
  "block w-full rounded-lg border-slate-300 text-sm shadow-sm transition duration-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15";
const textButtonClass = `shrink-0 rounded px-1.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:text-slate-400 disabled:hover:bg-transparent ${focusRing}`;

// Why a question cannot be chosen: the server refuses drafts, archived ones and the assignee's own.
const refusal = (question, assignee) =>
  question.status?.code === "DRAFT"
    ? "draft"
    : question.status?.code === "ARCHIVED"
      ? "archived"
      : assignee && question.author?.uuid === assignee
        ? "own"
        : "";

/**
 * The questions of a review task by chosen questions: the subject's bank, searched and by author, page by page, with a
 * checkbox each; the chosen ones above it, each removable (those of other pages too). `chosen`: a Map uuid → {uuid,
 * code, author}; `onChange(next)` gets a new one. `authors`: the subject's members who write, for the author filter.
 */
export default function TaskQuestionPicker({ folder, assignee, authors = [], chosen, onChange }) {
  const [t] = useTranslation();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [author, setAuthor] = useState("");
  const [page, setPage] = useState(1);
  // A retry reads the same page again.
  const [attempt, setAttempt] = useState(0);
  const [list, setList] = useState({ items: [], count: 0, loading: true, error: false });

  // The search waits for the typing to pause.
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    setList((prev) => ({ ...prev, items: page === 1 ? [] : prev.items, loading: true, error: false }));
    getQuestions({
      params: { folder: folder.uuid, page, page_size: PAGE_SIZE, search: query || undefined, author: author || undefined, ordering: "-created" },
      signal: controller.signal,
    })
      .then((data) =>
        setList((prev) => ({
          items: page === 1 ? data?.results || [] : [...prev.items, ...(data?.results || [])],
          count: data?.count || 0,
          loading: false,
          error: false,
        }))
      )
      .catch((err) => {
        if (!isCanceledError(err)) setList((prev) => ({ ...prev, loading: false, error: true }));
      });
    return () => controller.abort();
  }, [folder.uuid, query, author, page, attempt]);

  const full = chosen.size >= MAX_TASK_QUESTIONS;
  const choosable = list.items.filter((q) => !refusal(q, assignee));
  const allShownChosen = choosable.length > 0 && choosable.every((q) => chosen.has(q.uuid));

  const entry = (q) => ({ uuid: q.uuid, code: q.code, author: q.author?.uuid });
  const toggle = (q) => {
    const next = new Map(chosen);
    if (next.has(q.uuid)) next.delete(q.uuid);
    else if (!full) next.set(q.uuid, entry(q));
    onChange(next);
  };
  const remove = (uuid) => {
    const next = new Map(chosen);
    next.delete(uuid);
    onChange(next);
  };
  // The shown ones on or off, as many as the task may still take.
  const toggleShown = () => {
    const next = new Map(chosen);
    if (allShownChosen) choosable.forEach((q) => next.delete(q.uuid));
    else choosable.forEach((q) => next.size < MAX_TASK_QUESTIONS && next.set(q.uuid, entry(q)));
    onChange(next);
  };

  return (
    <fieldset>
      <legend className="mb-1.5 block text-sm font-medium text-slate-800">{t("folders.tasks.dialog.questions")}</legend>

      {chosen.size > 0 && (
        <div className="mb-3 rounded-lg bg-slate-50 p-2 ring-1 ring-inset ring-slate-200">
          <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
            <span className="font-medium tabular-nums" aria-live="polite">
              {t("folders.tasks.dialog.questions_chosen", { count: chosen.size })}
            </span>
            <button type="button" onClick={() => onChange(new Map())} className={textButtonClass}>
              {t("folders.tasks.dialog.questions_clear")}
            </button>
          </div>
          <ul className="mt-1.5 flex max-h-24 flex-wrap gap-1 overflow-y-auto">
            {[...chosen.values()].map((q) => (
              <li key={q.uuid}>
                <button
                  type="button"
                  onClick={() => remove(q.uuid)}
                  aria-label={t("folders.tasks.dialog.questions_remove", { code: q.code })}
                  className={`inline-flex min-h-[1.75rem] items-center gap-0.5 rounded-md bg-white pl-2 pr-1 text-xs font-medium tabular-nums text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-100 max-sm:min-h-9 ${focusRing}`}
                >
                  {q.code}
                  <XMarkIcon className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
          {full && <p className="mt-1.5 text-xs text-amber-800">{t("folders.tasks.dialog.questions_limit", { max: MAX_TASK_QUESTIONS })}</p>}
        </div>
      )}

      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <label className="relative block">
          <span className="sr-only">{t("folders.tasks.dialog.questions_search")}</span>
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("folders.tasks.dialog.questions_search")}
            className={`${fieldClass} h-10 pl-9 max-sm:h-11`}
          />
        </label>
        <label className="block">
          <span className="sr-only">{t("folders.tasks.dialog.questions_author")}</span>
          <select
            value={author}
            onChange={(e) => {
              setAuthor(e.target.value);
              setPage(1);
            }}
            className={`${fieldClass} h-10 max-sm:h-11`}
          >
            <option value="">{t("folders.tasks.dialog.questions_author_all")}</option>
            {authors.map((m) => (
              <option key={m.user.uuid} value={m.user.uuid}>
                {personName(m.user)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3 text-xs text-slate-500">
        <span className="tabular-nums">{list.loading && page === 1 ? "" : t("folders.tasks.dialog.questions_found", { count: list.count })}</span>
        {choosable.length > 0 && (
          <button type="button" onClick={toggleShown} disabled={!allShownChosen && full} className={textButtonClass}>
            {allShownChosen ? t("folders.tasks.dialog.questions_unpick_shown") : t("folders.tasks.dialog.questions_pick_shown")}
          </button>
        )}
      </div>

      <ul className="mt-1 max-h-72 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-1.5">
        {list.items.map((q) => {
          const why = refusal(q, assignee);
          const checked = chosen.has(q.uuid);
          return (
            <li key={q.uuid}>
              <label
                className={`flex min-h-[2.75rem] items-start gap-3 rounded-md px-2 py-1.5 ${
                  why || (full && !checked) ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-slate-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!!why || (full && !checked)}
                  onChange={() => toggle(q)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span className="font-medium tabular-nums text-slate-900">{q.code}</span>
                    <DifficultyBadge level={q.difficulty} showPoints={false} />
                    <QuestionStatusBadge status={q.status} />
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                    {[q.name, personName(q.author), why && t(`folders.tasks.dialog.questions_${why}`)].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
        {list.loading ? (
          <li role="status" className="px-2 py-3 text-center text-sm text-slate-500">
            {t("folders.tasks.dialog.loading")}
          </li>
        ) : list.error ? (
          <li className="flex items-center justify-between gap-3 rounded-md bg-red-50 px-2 py-2 text-sm text-red-700">
            {t("folders.tasks.dialog.questions_failed")}
            <button type="button" onClick={() => setAttempt((n) => n + 1)} className={textButtonClass}>
              {t("folders.tasks.retry")}
            </button>
          </li>
        ) : list.items.length === 0 ? (
          <li className="px-2 py-3 text-center text-sm text-slate-500">{t("folders.tasks.dialog.questions_empty")}</li>
        ) : (
          list.items.length < list.count && (
            <li>
              <button
                type="button"
                onClick={() => setPage((p) => p + 1)}
                className={`flex min-h-[2.5rem] w-full items-center justify-center rounded-md text-sm font-medium text-blue-700 hover:bg-blue-50 max-sm:min-h-11 ${focusRing}`}
              >
                {t("folders.tasks.dialog.questions_more", { count: list.count - list.items.length })}
              </button>
            </li>
          )
        )}
      </ul>
    </fieldset>
  );
}
